import { CheckCircle2, ChevronRight, Clock, MessageSquareText, Receipt, Search, TriangleAlert } from "lucide-react";

import TablePagination from "../../../components/TablePagination";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Skeleton } from "../../../components/ui/skeleton";
import { dataBR, hora, inteiro, reais } from "../lib/caixa";
import { diaDaSemana, duracao, porMes, resultadoDoCaixa } from "../lib/fechamento";
import { ESTILO_RESULTADO, SeloResultado } from "./ResultadoCaixa";

const FILTROS_RESULTADO = [
  { valor: "", rotulo: "Todos" },
  { valor: "bateu", rotulo: "Bateram" },
  { valor: "diferenca", rotulo: "Com diferença" },
];

/** Card do topo. Com `onClick` vira o filtro (Bateram / Com diferença): clicar de novo tira. */
function Numero({ icone: Icone, rotulo, valor, dica, cor = "", onClick, ativo, anel = "", e2e }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-e2e={e2e} aria-pressed={onClick ? !!ativo : undefined}
      className={`rounded-2xl border bg-card p-4 text-left transition-all ${onClick ? "cursor-pointer hover:bg-muted/40" : ""} ${ativo ? `ring-2 ${anel}` : ""}`}>
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Icone className="w-3.5 h-3.5" />{rotulo}</p>
      <p className={`text-2xl font-black tabular-nums mt-1 ${cor}`}>{valor}</p>
      {dica ? <p className="text-[11px] text-muted-foreground mt-0.5">{dica}</p> : null}
    </Tag>
  );
}

const CardsCarregando = () => (
  <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[104px] rounded-2xl" />)}</div>
);

/** Uma linha da lista: o dia, quem, quanto vendeu, quanto contou e se bateu. Tocar abre o fechamento. */
function LinhaCaixa({ h, onAbrir }) {
  const res = resultadoDoCaixa(h);
  const fechou = h.fechado_por && h.fechado_por !== h.usuario_nome ? h.fechado_por : null;
  return (
    <li>
      <button type="button" onClick={() => onAbrir(h)} data-e2e="caixa-historico"
        className={`w-full text-left px-3 sm:px-4 py-3 border-l-4 ${ESTILO_RESULTADO[res.tipo].faixa} hover:bg-muted/50 transition-colors flex items-center gap-3 sm:gap-4`}>
        <div className="w-14 shrink-0 text-center">
          <p className="text-[11px] uppercase text-muted-foreground font-semibold">{diaDaSemana(h.opened_at)}</p>
          <p className="text-lg font-black leading-tight tabular-nums">{dataBR(h.opened_at).slice(0, 5)}</p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">
            Caixa #{h.id}
            <span className="block sm:inline font-normal text-muted-foreground"><span className="hidden sm:inline"> · </span>{hora(h.opened_at)} → {hora(h.closed_at)}{duracao(h) ? ` (${duracao(h)})` : ""}</span>
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {h.usuario_nome || "—"}{fechou ? ` abriu · ${fechou} fechou` : ""}
          </p>
          {h.justificativa ? (
            <p className="text-xs mt-1 flex items-start gap-1 text-foreground/80" data-e2e="motivo-linha">
              <MessageSquareText className="w-3.5 h-3.5 shrink-0 mt-px text-muted-foreground" />
              <span className="italic truncate">“{h.justificativa}”</span>
            </p>
          ) : null}
          <div className="sm:hidden mt-1.5 flex items-center gap-2">
            <SeloResultado resultado={res} />
            <span className="text-xs text-muted-foreground tabular-nums">{reais(h.total_vendido)} vendido</span>
          </div>
        </div>
        <div className="hidden md:block text-right w-32 shrink-0">
          <p className="text-sm font-semibold tabular-nums">{reais(h.total_vendido)}</p>
          <p className="text-[11px] text-muted-foreground">{h.qtd_vendas || 0} {h.qtd_vendas === 1 ? "venda" : "vendas"}</p>
        </div>
        <div className="hidden sm:block text-right w-28 shrink-0">
          <p className="text-sm font-semibold tabular-nums">{h.valor_fechamento_real == null ? "—" : reais(h.valor_fechamento_real)}</p>
          <p className="text-[11px] text-muted-foreground">contado</p>
        </div>
        <div className="hidden sm:flex w-36 shrink-0 justify-end"><SeloResultado resultado={res} /></div>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>
    </li>
  );
}

/**
 * Aba Caixas anteriores (25/10/2026): números do topo, filtros e os caixas por
 * mês, 10 por página. Tudo vem do servidor (todos os caixas da loja).
 */
export default function HistoricoCaixas({ filtros, onAbrir }) {
  const lista = filtros.itens;
  const r = filtros.resumo;
  // Cards Bateram / Com diferença filtram a lista (dono, 25/10/2026)
  const alternar = (valor) => filtros.setFiltroResultado(filtros.filtroResultado === valor ? "" : valor);
  return (
    <div className="space-y-4" data-e2e="historico-caixas">
      {!r ? <CardsCarregando /> : (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Numero icone={Clock} rotulo="Caixas" valor={inteiro(r.total)} dica={`Vendido ${reais(r.vendido)}`}
            onClick={() => filtros.setFiltroResultado("")} ativo={!filtros.filtroResultado} anel="ring-primary" e2e="card-caixas" />
          <Numero icone={CheckCircle2} rotulo="Bateram" valor={inteiro(r.bateram)} cor="text-emerald-600 dark:text-emerald-400"
            dica={r.total ? `${Math.round((r.bateram / r.total) * 100)}% dos caixas` : "—"}
            onClick={() => alternar("bateu")} ativo={filtros.filtroResultado === "bateu"} anel="ring-emerald-500" e2e="card-bateram" />
          <Numero icone={TriangleAlert} rotulo="Com diferença" valor={inteiro(r.com_diferenca)} cor={r.com_diferenca ? "text-amber-600 dark:text-amber-400" : ""}
            dica={r.com_diferenca ? "Clique para ver só esses" : "Nenhum"}
            onClick={() => alternar("diferenca")} ativo={filtros.filtroResultado === "diferenca"} anel="ring-amber-500" e2e="card-com-diferenca" />
          <Numero icone={Receipt} rotulo="Faltou no total" valor={`−${reais(r.faltou)}`} cor={r.faltou ? "text-red-600 dark:text-red-400" : ""}
            dica={`Sobrou no total +${reais(r.sobrou)}`} />
        </div>
      )}

      <section className="rounded-2xl border bg-card overflow-hidden">
        <div className="p-3 sm:p-4 border-b flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 rounded-lg bg-muted p-1 mr-auto">
            {FILTROS_RESULTADO.map((f) => (
              <button key={f.valor || "todos"} type="button" onClick={() => filtros.setFiltroResultado(f.valor)} data-e2e={`filtro-resultado-${f.valor || "todos"}`}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${filtros.filtroResultado === f.valor ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
                {f.rotulo}
              </button>
            ))}
          </div>
          <div className="relative"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input value={filtros.filtroBusca} onChange={(e) => filtros.setFiltroBusca(e.target.value)} placeholder="Operador ou nº" className="pl-9 h-9 w-44 text-sm" /></div>
          <Input type="date" value={filtros.filtroData} onChange={(e) => filtros.setFiltroData(e.target.value)} className="h-9 w-40 text-sm" aria-label="Dia" />
          {filtros.temFiltroAtivo && <Button variant="ghost" size="sm" onClick={filtros.limparFiltros}>Limpar</Button>}
        </div>
        {filtros.carregando && !lista.length ? (
          <div className="divide-y">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="px-4 py-3"><Skeleton className="h-12 w-full" /></div>)}</div>
        ) : !lista.length ? <p className="p-6 text-sm text-muted-foreground">Nenhum caixa fechado{filtros.temFiltroAtivo ? " com esse filtro" : ""}.</p> : (
          porMes(lista).map((g) => (
            <div key={g.mes}>
              <div className="px-4 py-2 bg-muted/40 border-b flex items-baseline gap-2 text-xs">
                <span className="font-bold text-sm text-foreground">{g.mes}</span>
              </div>
              <ul className={`divide-y border-b last:border-b-0 transition-opacity ${filtros.carregando ? "opacity-60" : ""}`}>
                {g.caixas.map((h) => <LinhaCaixa key={h.id} h={h} onAbrir={onAbrir} />)}
              </ul>
            </div>
          ))
        )}
        {filtros.total > 0 && (
          <TablePagination currentPage={filtros.pagina} totalItems={filtros.total} itemsPerPage={filtros.porPagina}
            onPageChange={filtros.setPagina} onItemsPerPageChange={filtros.setPorPagina} opcoesPorPagina={[10, 20, 30, 50]} />
        )}
      </section>
    </div>
  );
}
